import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ChatInput from "@/components/chat/ChatInput";
import { useTranslation } from "@/hooks/useTranslation";
import { useLanguage } from "@/contexts/LanguageContext";
const Index = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage, setLanguage } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const leftEyeRef = useRef<SVGImageElement>(null);
  const rightEyeRef = useRef<SVGImageElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [buttonHover, setButtonHover] = useState<"explore" | "engage" | "intro" | null>(null);
  const leftEyeBaseX = 114;
  const leftEyeBaseY = 55;
  const rightEyeBaseX = 194;
  const rightEyeBaseY = 55;
  const eyeSize = 28;
  const maxOffset = 20;
  const movementDivisor = 35;
  useEffect(() => {
    document.body.style.zoom = "100%";
    return () => {
      document.body.style.zoom = "";
    };
  }, []);
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        setMousePosition({
          x: mouseX,
          y: mouseY
        });
        const leftEyeCenterX = leftEyeBaseX + eyeSize / 2;
        const leftEyeCenterY = leftEyeBaseY + eyeSize / 2;
        let offsetLX = (mouseX - leftEyeCenterX) / movementDivisor;
        let offsetLY = (mouseY - leftEyeCenterY) / movementDivisor;
        offsetLX = Math.max(Math.min(offsetLX, maxOffset), -maxOffset);
        offsetLY = Math.max(Math.min(offsetLY, maxOffset), -maxOffset);
        const rightEyeCenterX = rightEyeBaseX + eyeSize / 2;
        const rightEyeCenterY = rightEyeBaseY + eyeSize / 2;
        let offsetRX = (mouseX - rightEyeCenterX) / movementDivisor;
        let offsetRY = (mouseY - rightEyeCenterY) / movementDivisor;
        offsetRX = Math.max(Math.min(offsetRX, maxOffset), -maxOffset);
        offsetRY = Math.max(Math.min(offsetRY, maxOffset), -maxOffset);
        if (leftEyeRef.current) {
          leftEyeRef.current.setAttribute('x', String(leftEyeBaseX + offsetLX));
          leftEyeRef.current.setAttribute('y', String(leftEyeBaseY + offsetLY));
        }
        if (rightEyeRef.current) {
          rightEyeRef.current.setAttribute('x', String(rightEyeBaseX + offsetRX));
          rightEyeRef.current.setAttribute('y', String(rightEyeBaseY + offsetRY));
        }
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  const calculateEyeTransform = () => {
    if (buttonHover === "explore") {
      return `translate(-4px, 4px)`;
    } else if (buttonHover === "engage") {
      return `translate(4px, 4px)`;
    } else if (buttonHover === "intro") {
      return `translate(0px, 4px)`;
    }
    const maxMove = 4;
    const distanceX = mousePosition.x;
    const distanceY = mousePosition.y;
    const magnitude = Math.sqrt(distanceX * distanceX + distanceY * distanceY);
    let moveX = 0;
    let moveY = 0;
    if (magnitude > 0) {
      moveX = distanceX / magnitude * Math.min(magnitude / 25, maxMove);
      moveY = distanceY / magnitude * Math.min(magnitude / 25, maxMove);
    }
    return `translate(${moveX}px, ${moveY}px)`;
  };
  const handleSendMessage = (message: string) => {
    console.log("Message sent:", message);
  };
  const openFigmaDemo = () => {
    const figmaUrl = "https://embed.figma.com/proto/G2GjhVgon1YRrqV2sY0u4d/Private-Channel?node-id=3680-35401&p=f&scaling=min-zoom&content-scaling=fixed&page-id=3680%3A26268&starting-point-node-id=3680%3A33013&embed-host=share";
    window.open(figmaUrl, "_blank");
  };
  return <div className="min-h-screen bg-white flex flex-col items-center justify-center">
      <div className="mb-8 relative" ref={containerRef}>
        <motion.div className="relative" initial={{
        y: 0
      }} animate={{
        y: [0, 5, 0],
        transition: {
          repeat: Infinity,
          repeatType: "reverse",
          duration: 2
        }
      }} whileHover={{
        scale: 1.05
      }}>
          <svg ref={svgRef} width="334" height="206" viewBox="0 0 334 206" className="animate-fade-in">
            <image ref={leftEyeRef} href="/lovable-uploads/3e0cc269-0dd5-40b5-a1f0-0ebbe7e85733.png" width={eyeSize} height={eyeSize} x={leftEyeBaseX} y={leftEyeBaseY} />
            <image ref={rightEyeRef} href="/lovable-uploads/3e0cc269-0dd5-40b5-a1f0-0ebbe7e85733.png" width={eyeSize} height={eyeSize} x={rightEyeBaseX} y={rightEyeBaseY} />
            <image href="/lovable-uploads/e558779e-6685-47f2-a018-ca987a5b402c.png" width="334" height="206" x="0" y="0" />
          </svg>
        </motion.div>
      </div>

      <div className="mb-8 flex items-center">
        <ChatInput isLoading={false} isGibletPage={false} onSendMessage={handleSendMessage} onInputFocus={() => {}} />
      </div>

      <div className="flex space-x-8 mb-6">
        <motion.div className="w-48 h-24 bg-black text-white rounded-lg flex items-center justify-center cursor-pointer" whileHover={{
        scale: 1.05
      }} onClick={() => navigate("/intro")} onMouseEnter={() => setButtonHover("intro")} onMouseLeave={() => setButtonHover(null)}>
          <span className="text-lg font-bold">{t('intro')}</span>
        </motion.div>

        <motion.div className="w-48 h-24 bg-black text-white rounded-lg flex items-center justify-center cursor-pointer" whileHover={{
        scale: 1.05
      }} onClick={() => navigate("/giblet")} onMouseEnter={() => setButtonHover("engage")} onMouseLeave={() => setButtonHover(null)}>
          <span className="text-lg font-bold">{t('demo')}</span>
        </motion.div>
      </div>

      <div className="flex space-x-3 mt-4">
        {(['EN', 'FR', 'SP'] as const).map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              currentLanguage === lang
                ? 'bg-gray-200 text-gray-800'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            {lang}
          </button>
        ))}
      </div>
    </div>;
};
export default Index;